if (!customElements.get('product-installments-o2')) {
  const selectors = {
    tabs: '.buy-box-o2__mode',
    modeInput: '.buy-box-o2__mode-input',
    selectedModeInput: '.buy-box-o2__mode-input:checked',
    openButton: '[data-installments-open]',
    dialog: '[data-installments-dialog]',
    fallback: '[data-installments-fallback]',
    main: 'main',
    productInfo: 'product-info',
    qr: '[data-installments-qr]',
    storeList: '[data-pickup-store-list]',
  };
  const modes = {
    oneTime: 'one-time',
  };
  const selectedModes = new Map();
  const modeInputSelector = (mode) => `${selectors.modeInput}[value="${mode}"]`;
  const openButtonSelector = (dialog) => `[data-installments-open="${dialog}"]`;
  const componentSelector = (sectionId) => `product-installments-o2[data-section-id="${sectionId}"]`;

  customElements.define(
    'product-installments-o2',
    class ProductInstallmentsO2 extends HTMLElement {
      static get drawerTransitionMs() {
        const raw = getComputedStyle(document.documentElement).getPropertyValue('--o2-duration-drawer').trim();
        const value = Number.parseFloat(raw);
        if (Number.isNaN(value)) return 0;
        return raw.endsWith('ms') ? value : value * 1000;
      }

      connectedCallback() {
        if (this.installmentsO2Init) return;
        this.installmentsO2Init = true;
        this.installmentsO2Active = true;
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onModeChange = this.onModeChange.bind(this);
        this.onHostClick = this.onHostClick.bind(this);
        this.onDialogClick = this.onDialogClick.bind(this);
        this.onDialogKeyup = this.onDialogKeyup.bind(this);
        this.addEventListener('change', this.onModeChange);
        this.addEventListener('click', this.onHostClick);
        this.prepareForProductSwap();
        this.restoreSelectedMode();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.moveDialogsToBody();
        this.renderQrs();
        this.hideFallback();
        this.dataset.installmentsEnhanced = 'true';
      }

      disconnectedCallback() {
        this.deactivate();

        queueMicrotask(() => {
          if (!document.querySelector(componentSelector(this.dataset.sectionId))) {
            selectedModes.set(this.dataset.sectionId, modes.oneTime);
          }
        });
      }

      deactivate() {
        if (!this.installmentsO2Active) return;
        this.installmentsO2Active = false;
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
        this.removeEventListener('change', this.onModeChange);
        this.removeEventListener('click', this.onHostClick);
        this.teardownDialogs();
      }

      get tabs() {
        return this.querySelector(selectors.tabs);
      }

      onModeChange(event) {
        const modeInput = event.target.closest(selectors.modeInput);
        if (!modeInput || !this.contains(modeInput)) return;

        selectedModes.set(this.dataset.sectionId, modeInput.value);
      }

      prepareForProductSwap() {
        const productInfo = this.closest(selectors.productInfo);
        const directSibling = productInfo?.nextElementSibling;
        const outgoingMain = productInfo?.closest(selectors.main)?.nextElementSibling;
        const outgoingProductInfo = directSibling?.matches(selectors.productInfo)
          ? directSibling
          : Array.from(outgoingMain?.querySelectorAll(selectors.productInfo) || []).find(
              (candidate) => candidate.dataset.section === productInfo?.dataset.section
            );
        if (!outgoingProductInfo || outgoingProductInfo.dataset.section !== productInfo.dataset.section) return;

        const outgoingComponent = outgoingProductInfo.querySelector(componentSelector(this.dataset.sectionId));
        if (outgoingComponent) {
          // Dawn keeps the outgoing swap DOM for 500 ms, but its body-level drawers
          // and pub/sub listener must not overlap the newly connected component.
          outgoingComponent.deactivate();
        } else {
          selectedModes.set(this.dataset.sectionId, modes.oneTime);
        }
      }

      restoreSelectedMode() {
        const selectedMode = selectedModes.get(this.dataset.sectionId);
        if (!selectedMode) {
          const initialMode = this.querySelector(selectors.selectedModeInput)?.value;
          if (initialMode) selectedModes.set(this.dataset.sectionId, initialMode);
          return;
        }

        const modeInput = this.querySelector(modeInputSelector(selectedMode));
        if (modeInput) modeInput.checked = true;
      }

      setExpandedDialog(name) {
        this.querySelectorAll(selectors.openButton).forEach((button) => {
          button.setAttribute('aria-expanded', String(button.dataset.installmentsOpen === name));
        });
      }

      onVariantChange(event) {
        if (event?.data?.sectionId !== this.dataset.sectionId) return;

        const destination = this.querySelector(`#Installments-${this.dataset.sectionId}`);
        if (!destination) return;

        const source = event.data.html.getElementById(`Installments-${this.dataset.sectionId}`);
        const focusDialog = this.activeDialog?.dataset.installmentsDialog;
        if (focusDialog) this.focusElement = null;
        this.teardownDialogs();

        // The new variant does not offer installments. Hide the switcher and fall
        // back to one-time, otherwise the buy buttons stay hidden behind a mode
        // that no longer applies and the panel keeps the previous variant's terms.
        if (!source) {
          this.selectOneTimeMode();
          if (this.tabs) this.tabs.hidden = true;
          destination.innerHTML = '';
          return;
        }

        if (this.tabs) this.tabs.hidden = false;
        destination.innerHTML = source.innerHTML;
        this.moveDialogsToBody();
        this.renderQrs();
        this.hideFallback();
        if (focusDialog) this.querySelector(openButtonSelector(focusDialog))?.focus();
      }

      selectOneTimeMode() {
        const oneTime = this.querySelector(modeInputSelector(modes.oneTime));
        if (oneTime && !oneTime.checked) oneTime.checked = true;
        selectedModes.set(this.dataset.sectionId, modes.oneTime);
      }

      onHostClick(event) {
        const openButton = event.target.closest(selectors.openButton);
        if (openButton && this.contains(openButton)) {
          this.openDialog(openButton.dataset.installmentsOpen, openButton);
        }
      }

      moveDialogsToBody() {
        this.dialogs = new Map();
        this.querySelectorAll(selectors.dialog).forEach((dialog) => {
          this.dialogs.set(dialog.dataset.installmentsDialog, dialog);
          document.body.appendChild(dialog);
          dialog.addEventListener('click', this.onDialogClick);
          dialog.addEventListener('keyup', this.onDialogKeyup);
          dialog
            .querySelectorAll(selectors.storeList)
            .forEach((list) => window.PickupAvailabilityO2?.initStoreList(list));
        });
      }

      teardownDialogs() {
        this.closeDialog();
        this.dialogs?.forEach((dialog) => dialog.remove());
        this.dialogs = null;
      }

      onDialogClick(event) {
        const closeButton = event.target.closest('[data-installments-close]');
        if (closeButton && event.currentTarget.contains(closeButton)) {
          this.closeDialog();
          return;
        }

        const switchButton = event.target.closest('[data-installments-switch]');
        if (switchButton && event.currentTarget.contains(switchButton)) {
          this.switchDialog(switchButton.dataset.installmentsSwitch);
        }
      }

      onDialogKeyup(event) {
        if (event.code?.toUpperCase() === 'ESCAPE') this.closeDialog();
      }

      openDialog(name, focusElement) {
        const dialog = this.dialogs?.get(name);
        if (!dialog) return;
        if (dialog.hasAttribute('open')) return;

        this.focusElement = focusElement;
        this.setExpandedDialog(name);
        this.activeDialog = dialog;
        this.trackSetupViewport();
        dialog.hidden = false;
        document.body.classList.add('overflow-hidden');
        requestAnimationFrame(() => {
          if (this.activeDialog === dialog) dialog.setAttribute('open', '');
        });
        trapFocus(dialog);
      }

      switchDialog(name) {
        const nextDialog = this.dialogs?.get(name);
        if (!this.activeDialog || !nextDialog || nextDialog === this.activeDialog) return;

        const previousDialog = this.activeDialog;
        nextDialog.hidden = false;
        this.activeDialog = nextDialog;
        this.trackSetupViewport();
        this.focusElement = this.querySelector(openButtonSelector(name)) || this.focusElement;
        this.setExpandedDialog(name);
        requestAnimationFrame(() => {
          if (this.activeDialog === nextDialog) nextDialog.setAttribute('open', '');
          previousDialog.removeAttribute('open');
          previousDialog.hidden = true;
        });
        trapFocus(nextDialog);
      }

      closeDialog() {
        const dialog = this.activeDialog;
        if (!dialog) return;

        dialog.removeAttribute('open');
        this.activeDialog = null;
        this.stopTrackingSetupViewport();
        document.body.classList.remove('overflow-hidden');
        this.setExpandedDialog();
        removeTrapFocus(this.focusElement?.isConnected ? this.focusElement : null);
        window.setTimeout(() => {
          if (!dialog.hasAttribute('open')) dialog.hidden = true;
        }, ProductInstallmentsO2.drawerTransitionMs);
      }

      trackSetupViewport() {
        this.stopTrackingSetupViewport();
        const viewport = window.visualViewport;
        const dialog = this.activeDialog;
        if (!viewport || dialog?.dataset.installmentsDialog !== 'setup') return;

        this.viewportDialog = dialog;
        this.viewportController = new AbortController();
        const update = () => {
          // Pinch zoom must retain the browser's normal pan/zoom behavior.
          if (viewport.scale !== 1) {
            dialog.style.removeProperty('--installments-viewport-top');
            dialog.style.removeProperty('--installments-viewport-height');
            return;
          }
          dialog.style.setProperty('--installments-viewport-top', `${viewport.offsetTop}px`);
          dialog.style.setProperty('--installments-viewport-height', `${viewport.height}px`);
        };
        const options = { signal: this.viewportController.signal };
        viewport.addEventListener('resize', update, options);
        viewport.addEventListener('scroll', update, options);
        update();
      }

      stopTrackingSetupViewport() {
        this.viewportController?.abort();
        this.viewportController = null;
        this.viewportDialog?.style.removeProperty('--installments-viewport-top');
        this.viewportDialog?.style.removeProperty('--installments-viewport-height');
        this.viewportDialog = null;
      }

      hideFallback() {
        const fallback = this.querySelector(selectors.fallback);
        if (fallback) fallback.hidden = true;
      }

      renderQrs() {
        if (typeof QRCode !== 'function') {
          console.warn('QRCode library is not available for product installments.');
          return;
        }

        [this, ...(this.dialogs?.values() || [])].forEach((root) => {
          root.querySelectorAll(selectors.qr).forEach((container) => {
            if (container.dataset.qrReady === 'true') return;

            new QRCode(container, {
              text: container.dataset.identifier,
              width: 80,
              height: 80,
              imageAltText: container.dataset.alt,
            });
            container.dataset.qrReady = 'true';
          });
        });
      }
    }
  );
}
